import { createFileRoute } from '@tanstack/react-router';
import { useTranslation } from 'react-i18next';
import { z } from 'zod';
import { useEffect, useState, useMemo } from 'react';
import {
  Button,
  Dropdown,
  Icon,
  Input,
  Payment,
  PaymentList,
  Spinner,
  Tabs,
} from '@penta/penta-shared';
import { usePentaRq } from '@penta/penta-api';
import type { PaymentProps } from '@penta/penta-shared';
import { useClient } from '../../../../contexts/ClientContext';
import { useRedirectToOldIb } from '../../../../hooks/useRedirectToOldIb';
import { FEATURES } from '../../../../constants/featureFlags';
import styles from '../payments.module.scss';

const ACCOUNT_SELECT_TRIGGER_STYLE = {
  width: '100%',
  justifyContent: 'space-between',
};

const paymentsSearchSchema = z.object({
  productId: z.coerce.number().optional().catch(undefined),
  pageIndex: z.coerce.number().default(0).catch(0),
  searchQuery: z.string().optional().catch(''),
});

export const Route = createFileRoute('/$locale/smartbanking/payments/')({
  validateSearch: paymentsSearchSchema,
  component: PaymentsList,
});

export function PaymentsList() {
  const { t } = useTranslation();
  const navigate = Route.useNavigate();
  const searchParams = Route.useSearch();
  const { locale } = Route.useParams();
  const { selectedClientId } = useClient();
  const api = usePentaRq();

  // Temporarily unused — will be used when search feature is enabled
  // const [localSearchQuery, setLocalSearchQuery] = useState(
  //   searchParams.searchQuery || '',
  // );

  // Derive a guaranteed number; -1 is never actually sent because enabled guards the call
  const productClientId = selectedClientId ?? -1;

  // 1. Fetch Accounts (Products)
  const { data: productsData, isLoading: isLoadingProducts } = api.useQuery(
    'get',
    '/product',
    { params: { query: { clientId: productClientId } } },
    { enabled: typeof selectedClientId === 'number' },
  );

  const currentAccounts = useMemo(
    () => productsData?.currentAccounts || [],
    [productsData],
  );

  // Auto-select first account if not present in URL
  useEffect(() => {
    if (!searchParams.productId && currentAccounts.length > 0) {
      void navigate({
        search: {
          productId: currentAccounts[0].id,
          pageIndex: searchParams.pageIndex,
          searchQuery: searchParams.searchQuery,
        },
        replace: true,
      });
    }
  }, [
    searchParams.productId,
    searchParams.pageIndex,
    searchParams.searchQuery,
    currentAccounts,
    navigate,
  ]);

  // 2. Map items for Dropdown
  const dropdownItems = useMemo(() => {
    const items = currentAccounts.map((acc) => ({
      id: String(acc.id),
      label: `${acc.name} (${acc.iban})`,
      disabled: false,
    }));

    if (FEATURES.ENABLE_ALL_ACCOUNTS) {
      items.unshift({
        id: 'all',
        label: t('payments.allAccounts', 'Všechny účty'),
        disabled: false,
      });
    } else {
      items.unshift({
        id: 'all',
        label: t('payments.allAccounts', 'Všechny účty'),
        disabled: true, // Disabled per feature flag
      });
    }
    return items;
  }, [currentAccounts, t]);

  // 3. Fetch Payments
  const { data: paymentsData, isLoading: isLoadingPayments } = api.useQuery(
    'get',
    '/payment',
    {
      params: {
        query: {
          productId: searchParams.productId,
          pageIndex: searchParams.pageIndex,
          pageItemCount: 10,
        },
      },
    },
    {
      enabled: !!searchParams.productId && !FEATURES.ENABLE_TRANSACTION_SEARCH,
    },
  );

  const [accumulatedPayments, setAccumulatedPayments] = useState<
    typeof paymentsData
  >([]);

  // Reset accumulated payments when the selected account changes
  useEffect(() => {
    setAccumulatedPayments([]);
  }, [searchParams.productId]);

  useEffect(() => {
    if (paymentsData) {
      if (searchParams.pageIndex === 0) {
        setAccumulatedPayments(paymentsData);
      } else {
        setAccumulatedPayments((prev) => {
          if (!prev) return paymentsData;
          const newIds = new Set(paymentsData.map((p) => p.id));
          const filteredPrev = prev.filter((p) => !newIds.has(p.id));
          return [...filteredPrev, ...paymentsData];
        });
      }
    }
  }, [paymentsData, searchParams.pageIndex]);

  // 4. Map payments to UI format
  const mappedPayments: PaymentProps[] = useMemo(() => {
    if (!accumulatedPayments) return [];

    return accumulatedPayments.map((p) => {
      const type = (p.paymentOrder?.amount ?? 0) < 0 ? 'outgoing' : 'incoming';
      const amount = Math.abs(p.paymentOrder?.amount ?? 0);

      return {
        type,
        title:
          p.paymentOrder?.partner?.name ||
          p.paymentOrder?.partner?.iban ||
          'Neznámý',
        date: p.createdDate,
        transactionNumber: String(p.id),
        amount,
        currency: p.paymentOrder?.currency || 'CZK',
        'data-testid': `payment-item-${p.id}`,
      };
    });
  }, [accumulatedPayments]);

  // Handlers
  const handleAccountChange = (value: string) => {
    void navigate({
      search: {
        productId: value === 'all' ? undefined : Number(value),
        pageIndex: 0,
        searchQuery: searchParams.searchQuery,
      },
    });
  };

  const handleLoadMore = () => {
    void navigate({
      search: {
        productId: searchParams.productId,
        pageIndex: searchParams.pageIndex + 1,
        searchQuery: searchParams.searchQuery,
      },
    });
  };

  const { redirect: redirectToOldIb } = useRedirectToOldIb();

  const tabsItems = useMemo(
    () => [
      { id: 'payments', label: t('payments.tabs.payments', 'Platby') },
      {
        id: 'standing-orders',
        label: t('payments.tabs.standingOrders', 'Trvalé příkazy (SEPA)'),
      },
      {
        id: 'direct-debits',
        label: t('payments.tabs.directDebits', 'Inkasa (SEPA)'),
      },
      {
        id: 'orders-to-sign',
        label: t('payments.tabs.ordersToSign', 'Zprávy k podepsání'),
      },
    ],
    [t],
  );

  const handleTabChange = (tabId: string) => {
    if (tabId === 'payments') return;
    const pageMap: Record<string, string> = {
      'standing-orders': 'StandingOrderList',
      'direct-debits': 'SddMandateList',
      'orders-to-sign': 'AccountOrderToSignList',
    };
    const pageIdentifier = pageMap[tabId];
    if (pageIdentifier) void redirectToOldIb(pageIdentifier);
  };

  return (
    <>
      <header className={styles.pageHeader}>
        <h1 className={styles.pageTitle}>{t('payments.title', 'Platby')}</h1>
        {FEATURES.ENABLE_PAYMENT_TABS && (
          <Tabs
            items={tabsItems}
            value="payments"
            onValueChange={handleTabChange}
            className={styles.paymentTabs}
          />
        )}
      </header>

      <section className={styles.controlsRow}>
        {FEATURES.ENABLE_TRANSACTION_SEARCH && (
          <div className={styles.searchContainer}>
            <Input
              type="text"
              placeholder={t('payments.searchPlaceholder', 'Vyhledat...')}
              aria-label={t('payments.searchAriaLabel', 'Vyhledat platby')}
              iconLeft={<Icon name="magnifying-glass" size={20} aria-hidden />}
              data-testid="payments-search-input"
            />
          </div>
        )}

        <div className={styles.accountSelect}>
          {isLoadingProducts ? (
            <Spinner />
          ) : (
            <Dropdown
              trigger={
                <Button
                  variant="outline"
                  style={ACCOUNT_SELECT_TRIGGER_STYLE}
                  iconRight={<Icon name="chevron-down" size={16} />}
                  data-testid="payments-account-select-trigger"
                >
                  {dropdownItems.find(
                    (item) =>
                      item.id ===
                      (searchParams.productId
                        ? String(searchParams.productId)
                        : 'all'),
                  )?.label ?? t('payments.allAccounts', 'Všechny účty')}
                </Button>
              }
              items={dropdownItems}
              value={
                searchParams.productId ? String(searchParams.productId) : 'all'
              }
              onValueChange={handleAccountChange}
              data-testid="payments-account-select"
            />
          )}
        </div>

        {FEATURES.ENABLE_TRANSACTION_SEARCH && (
          <Button
            variant="outline"
            className={styles.filterButton}
            iconOnly
            iconLeft={<Icon name="adjustments-horizontal" size={20} />}
            aria-label={t('payments.filterLabel', 'Filtry')}
            data-testid="payments-filter-button"
          />
        )}
      </section>

      <section className={styles.paymentsList}>
        {isLoadingPayments && searchParams.pageIndex === 0 ? (
          <Spinner />
        ) : (
          <>
            <PaymentList
              payments={mappedPayments}
              data-testid="payments-list"
              renderPayment={(payment: PaymentProps) => {
                const url = `/${locale}/smartbanking/payments/${payment.transactionNumber}`;
                return (
                  <Payment
                    key={payment.transactionNumber}
                    {...payment}
                    href={url}
                    data-testid={payment['data-testid']}
                  />
                );
              }}
            />
            {paymentsData && paymentsData.length === 10 && (
              <div className={styles.loadMoreContainer}>
                <Button
                  variant="outline"
                  onClick={handleLoadMore}
                  data-testid="payments-load-more"
                  loading={isLoadingPayments}
                >
                  {t('payments.loadMore', 'Zobrazit další')}
                </Button>
              </div>
            )}
          </>
        )}
      </section>
    </>
  );
}
